import type { ApiClient } from '../client';
import type { ApiResponse, CreateGradingRuleRequest, CreateGradingRuleResponse, CreateReportCardTemplateRequest, CreateReportCardTemplateResponse, GenerateReportCardRequest, GenerateReportCardResponse, ListGradingRulesResponse, ListReportCardTemplatesResponse, PurgeExpiredReportCardsResponse, RenderReportCardResponse, Uuid, UpdateReportCardTemplateRequest, ValidateReportCardTemplateRequest, ValidateReportCardTemplateResponse } from '../types';
import type { PaginationQuery } from '../../../types/pagination';
import { appendPaginationQuery } from '../../../types/pagination';

export function bindReportCards(client: ApiClient) {
  return {
async listReportCardTemplates(term?: string): Promise<ApiResponse<ListReportCardTemplatesResponse>> {
    const q = term?.trim() ? `?term=${encodeURIComponent(term.trim())}` : '';
    return client.request<ListReportCardTemplatesResponse>(`/report-cards/templates${q}`);
  },

async createReportCardTemplate(
    body: CreateReportCardTemplateRequest
  ): Promise<ApiResponse<CreateReportCardTemplateResponse>> {
    return client.request<CreateReportCardTemplateResponse>('/report-cards/templates', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async deleteReportCardTemplate(templateId: Uuid): Promise<ApiResponse<void>> {
    return client.request<void>(`/report-cards/templates/${encodeURIComponent(templateId)}`, {
      method: 'DELETE',
    });
  },

async updateReportCardTemplate(
    templateId: Uuid,
    body: UpdateReportCardTemplateRequest
  ): Promise<ApiResponse<void>> {
    return client.request<void>(`/report-cards/templates/${encodeURIComponent(templateId)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

async validateReportCardTemplate(
    body: ValidateReportCardTemplateRequest
  ): Promise<ApiResponse<ValidateReportCardTemplateResponse>> {
    return client.request<ValidateReportCardTemplateResponse>('/report-cards/templates/validate', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listGradingRules(params?: PaginationQuery): Promise<ApiResponse<ListGradingRulesResponse>> {
    const qs = new URLSearchParams();
    appendPaginationQuery(qs, params);
    const query = qs.toString();
    return client.request<ListGradingRulesResponse>(
      query ? `/report-cards/grading-rules?${query}` : '/report-cards/grading-rules'
    );
  },

async createGradingRule(body: CreateGradingRuleRequest): Promise<ApiResponse<CreateGradingRuleResponse>> {
    return client.request<CreateGradingRuleResponse>('/report-cards/grading-rules', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async generateReportCard(body: GenerateReportCardRequest): Promise<ApiResponse<GenerateReportCardResponse>> {
    return client.request<GenerateReportCardResponse>('/report-cards/generate', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async renderReportCard(reportCardId: Uuid): Promise<ApiResponse<RenderReportCardResponse>> {
    return client.request<RenderReportCardResponse>(`/report-cards/${encodeURIComponent(reportCardId)}/render`);
  },

async downloadReportCardPdf(reportCardId: Uuid): Promise<Blob> {
    const path = `/report-cards/${encodeURIComponent(reportCardId)}/pdf`;
    const url = `${client.baseUrl}${path}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: client.authHeaders(),
    });
    if (!response.ok) {
      throw new Error(`PDF download failed: ${response.status}`);
    }
    return response.blob();
  },

async purgeExpiredReportCards(): Promise<ApiResponse<PurgeExpiredReportCardsResponse>> {
    return client.request<PurgeExpiredReportCardsResponse>('/report-cards/purge-expired', {
      method: 'POST',
    });
  },

// Schedule API
  async getSchedule(params?: {
    startDate?: string;
    endDate?: string;
    teacherId?: string;
    grade?: string;
  }): Promise<ApiResponse<any[]>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const endpoint = queryParams.toString() 
      ? `/api/schedule?${queryParams.toString()}` 
      : '/api/schedule';
    
    return client.request(endpoint);
  },

// Grades API
  async getGrades(params?: {
    studentId?: string;
    courseId?: string;
    term?: string;
  }): Promise<ApiResponse<any[]>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const endpoint = queryParams.toString() 
      ? `/api/grades?${queryParams.toString()}` 
      : '/api/grades';
    
    return client.request(endpoint);
  }
  };
}
